import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Common } from "../../../utils";
import { addDays, formatRangeLabel, toUtcDateOnlyISO } from "./timesheetDateUtils";
import {
  fetchTimesheetProjects,
  mapTimesheetProjectsResponse,
  handleTimesheetApiError,
} from "./timesheetApi";
import "./TimeSheets.css";
import "../documents/Documents.css";

/** Max hours per calendar day across all lines (client-side guard). */
const MAX_HOURS_PER_DAY = 24;

const round2 = (n) => Math.round(n * 100) / 100;

const parseHours = (v) => {
  if (v === "" || v == null) return null;
  const s = String(v).replace(",", ".").trim();
  if (s === "" || s === "." || s === "-" || s === "-.") return null;
  if (/^\d+\.$/.test(s)) return null;
  const n = Number(s);
  if (!Number.isFinite(n)) return null;
  return n;
};

const formatHourDisplay = (h) => {
  if (h == null || Number.isNaN(h)) return "";
  const r = round2(h);
  return parseFloat(r.toFixed(2)).toString();
};

const sumRow = (hours) =>
  hours.reduce((acc, h) => acc + (typeof h === "number" && !Number.isNaN(h) ? h : 0), 0);

const sumCol = (rows, dayIndex) =>
  rows.reduce((acc, r) => acc + (typeof r.hours[dayIndex] === "number" ? r.hours[dayIndex] : 0), 0);

const EXPECTED_WEEKLY_HOURS = 40;

const cellKey = (rowId, dayIndex) => `${rowId}:${dayIndex}`;

const colorFromProjectId = (projectId) => {
  if (!projectId) return "#a8b4c2";
  let h = 0;
  for (let i = 0; i < projectId.length; i++) h = projectId.charCodeAt(i) + ((h << 5) - h);
  const hue = Math.abs(h) % 360;
  return `hsl(${hue} 26% 58%)`;
};

/** Local calendar day → ISO string for API (noon UTC on that civil date). */
const workDateISO = (date) => {
  const d = new Date(date);
  const y = d.getFullYear();
  const m = d.getMonth();
  const day = d.getDate();
  return new Date(Date.UTC(y, m, day, 12, 0, 0)).toISOString();
};

function clampCellForDay(parsed, rows, rowId, dayIndex) {
  if (parsed == null) return null;
  let v = round2(Math.min(MAX_HOURS_PER_DAY, Math.max(0, parsed)));
  const sumOthers = rows.reduce((acc, r) => {
    if (r.id === rowId) return acc;
    const x = r.hours[dayIndex];
    return acc + (typeof x === "number" && !Number.isNaN(x) ? x : 0);
  }, 0);
  const maxAllowed = round2(Math.max(0, MAX_HOURS_PER_DAY - sumOthers));
  if (v > maxAllowed) v = maxAllowed;
  return v;
}

/**
 * Weekly hour grid + utilization summary — shared layout for the full entry screen.
 * Primary CTA: submit / unsubmit / re-submit / blocked; optional colored status banner for a11y.
 */
export const SUBMIT_ACTION = {
  SUBMIT: "submit",
  UNSUBMIT: "unsubmit",
  RESUBMIT: "resubmit",
  SUBMIT_BLOCKED: "submit_blocked",
};

/** Colored a11y status strip on the entry screen (set from TimeSheetEntryPage). */
export const WEEKLY_STATUS_BANNER = {
  APPROVED: "approved",
  REJECTED_BLOCKED: "rejected_blocked",
  REJECTED_RESUBMIT: "rejected_resubmit",
  RETURNED: "returned",
  SUBMITTED: "submitted",
};

const TimeSheetWeeklyEntryForm = ({
  weekStart,
  onSaveDraft,
  onSubmit,
  onUnsubmit,
  submitActionKind = SUBMIT_ACTION.SUBMIT,
  showAddRow = true,
  savingMode = null,
  projects: projectsProp,
  projectsLoading: projectsLoadingProp,
  projectsError: projectsErrorProp,
  initialRows = [],
  weeklyLoading = false,
  readOnly = false,
  statusBannerKind = null,
  /** JS Date.getDay() 0..6; undefined = still loading (shows Sat/Sun until known). */
  officeOffDays,
}) => {
  const saving = savingMode !== null;
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [hourDraftByCell, setHourDraftByCell] = useState({});
  const [internalProjects, setInternalProjects] = useState([]);
  const [internalProjectsLoading, setInternalProjectsLoading] = useState(true);
  const [internalProjectsError, setInternalProjectsError] = useState(null);

  const useExternalProjects = projectsProp !== undefined;
  const projects = useExternalProjects ? projectsProp : internalProjects;
  const projectsLoading = useExternalProjects ? Boolean(projectsLoadingProp) : internalProjectsLoading;
  const projectsError = useExternalProjects ? projectsErrorProp ?? null : internalProjectsError;

  useEffect(() => {
    setRows([]);
    setHourDraftByCell({});
  }, [weekStart]);

  useEffect(() => {
    if (weeklyLoading) return;
    setRows(Array.isArray(initialRows) ? initialRows : []);
    setHourDraftByCell({});
  }, [weeklyLoading, initialRows]);

  useEffect(() => {
    if (useExternalProjects) return;
    let cancelled = false;
    (async () => {
      setInternalProjectsLoading(true);
      setInternalProjectsError(null);
      try {
        const raw = await fetchTimesheetProjects();
        if (cancelled) return;
        const list = mapTimesheetProjectsResponse(raw);
        setInternalProjects(list);
        if (raw && raw.success === false) {
          setInternalProjectsError(raw.message || raw.Message || t("app.timesheets.entry.projectsLoadFailed"));
        }
      } catch (e) {
        if (!cancelled) {
          setInternalProjectsError(t("app.timesheets.entry.projectsLoadFailed"));
          handleTimesheetApiError(e, t("app.timesheets.entry.projectsLoadFailed"));
        }
      } finally {
        if (!cancelled) setInternalProjectsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t, useExternalProjects]);

  const weekEnd = useMemo(() => addDays(weekStart, 6), [weekStart]);
  const dayDates = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);

  const offDaysSet = useMemo(() => {
    if (officeOffDays === undefined) return new Set([0, 6]);
    if (!Array.isArray(officeOffDays)) return new Set([0, 6]);
    return new Set(officeOffDays.filter((n) => Number.isInteger(n) && n >= 0 && n <= 6));
  }, [officeOffDays]);

  const columnIsNonBusinessDay = useCallback(
    (dayIndex) => offDaysSet.has(dayDates[dayIndex].getDay()),
    [offDaysSet, dayDates]
  );

  const dailyTotals = useMemo(() => dayDates.map((_, i) => sumCol(rows, i)), [rows, dayDates]);
  const grandTotal = useMemo(() => dailyTotals.reduce((a, b) => a + b, 0), [dailyTotals]);

  const { billableHours, nonBillableHours } = useMemo(() => {
    let bill = 0;
    let non = 0;
    rows.forEach((r) => {
      r.hours.forEach((h, di) => {
        if (typeof h !== "number" || Number.isNaN(h)) return;
        if (r.isBillable) bill += h;
        else non += h;
      });
    });
    return { billableHours: bill, nonBillableHours: non };
  }, [rows]);

  const utilizationPct = Math.min(100, Math.round((grandTotal / EXPECTED_WEEKLY_HOURS) * 100));
  const rangeLabel = formatRangeLabel(weekStart, weekEnd);

  /** Project ids already chosen on other rows (same week); exclude from each unpicked row’s dropdown. */
  const usedProjectIds = useMemo(
    () => new Set(rows.filter((r) => r.projectId).map((r) => String(r.projectId))),
    [rows]
  );

  const projectsForUnpickedRow = useCallback(
    (rowId) => {
      const taken = new Set(
        rows.filter((r) => r.id !== rowId && r.projectId).map((r) => String(r.projectId))
      );
      return projects.filter((p) => !taken.has(String(p.id)));
    },
    [rows, projects]
  );

  /** True when every loaded project already has a line — cannot add another distinct project line. */
  const cannotAddAnotherProjectLine = useMemo(() => {
    if (!projects.length) return false;
    return !projects.some((p) => !usedProjectIds.has(String(p.id)));
  }, [projects, usedProjectIds]);

  const commitCell = (rowId, dayIndex, raw) => {
    setRows((prev) => {
      const parsed = parseHours(raw);
      const nextVal = clampCellForDay(parsed, prev, rowId, dayIndex);
      return prev.map((r) =>
        r.id === rowId ? { ...r, hours: r.hours.map((h, i) => (i === dayIndex ? nextVal : h)) } : r
      );
    });
  };

  const addProjectLine = () => {
    const id = `r-${Date.now()}`;
    setRows((prev) => [
      ...prev,
      {
        id,
        projectId: null,
        projectName: "",
        teamName: "",
        detail: "",
        isBillable: true,
        color: "#94a3b8",
        hours: [null, null, null, null, null, null, null],
      },
    ]);
  };

  const setRowProject = (rowId, projectKey) => {
    const p = projects.find((x) => String(x.id) === String(projectKey));
    if (!p) return;
    setRows((prev) =>
      prev.map((r) =>
        r.id === rowId
          ? {
              ...r,
              projectId: p.id,
              projectName: p.name,
              teamName: p.teamName,
              color: colorFromProjectId(p.id),
            }
          : r
      )
    );
  };

  const setRowDetail = (rowId, detail) => {
    setRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, detail } : r)));
  };

  const setRowBillable = (rowId, isBillable) => {
    setRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, isBillable } : r)));
  };

  const removeRow = (rowId) => {
    setHourDraftByCell((prev) => {
      const prefix = `${rowId}:`;
      const next = { ...prev };
      Object.keys(next).forEach((k) => {
        if (k.startsWith(prefix)) delete next[k];
      });
      return next;
    });
    setRows((prev) => prev.filter((r) => r.id !== rowId));
  };

  const buildLogTimeSheetRequest = useCallback(() => {
    const entries = [];
    rows.forEach((r) => {
      if (!r.projectId) return;
      r.hours.forEach((h, di) => {
        if (typeof h !== "number" || Number.isNaN(h) || h <= 0) return;
        entries.push({
          projectId: String(r.projectId),
          workDate: workDateISO(dayDates[di]),
          hoursWorked: round2(h),
          taskDescription: (r.detail || "").trim().slice(0, 1000),
          isBillable: r.isBillable !== false,
        });
      });
    });
    return {
      weekStartDate: toUtcDateOnlyISO(weekStart),
      weekEndDate: toUtcDateOnlyISO(weekEnd),
      entries,
    };
  }, [rows, dayDates, weekStart, weekEnd]);

  const validateForSave = () => {
    // Hours without a project (shouldn’t happen while inputs are hidden until a project is chosen)
    const incomplete = rows.some((r) => r.hours.some((h) => typeof h === "number" && h > 0) && !r.projectId);
    if (incomplete) {
      return t("app.timesheets.entry.validationProjectRequired");
    }
    const req = buildLogTimeSheetRequest();
    // Lines with a project but no hours are treated as zero and are not sent; need ≥1 hour somewhere in the week
    if (!req.entries.length) {
      return t("app.timesheets.entry.validationHoursRequired");
    }
    return null;
  };

  const handleSaveDraftClick = () => {
    if (readOnly || weeklyLoading) return;
    const err = validateForSave();
    if (err) {
      Common.showErrorMessage(err);
      return;
    }
    if (onSaveDraft) onSaveDraft(buildLogTimeSheetRequest());
  };

  const handleSubmitClick = () => {
    if (readOnly || weeklyLoading || submitActionKind === SUBMIT_ACTION.SUBMIT_BLOCKED) return;
    const err = validateForSave();
    if (err) {
      Common.showErrorMessage(err);
      return;
    }
    if (onSubmit) onSubmit(buildLogTimeSheetRequest());
  };

  const handlePrimaryActionClick = () => {
    if (readOnly || weeklyLoading || submitActionKind === SUBMIT_ACTION.SUBMIT_BLOCKED) return;
    if (submitActionKind === SUBMIT_ACTION.UNSUBMIT) {
      if (onUnsubmit) onUnsubmit();
      return;
    }
    handleSubmitClick();
  };

  const dashOffset = 364.4 * (1 - utilizationPct / 100);

  const formLocked = saving || readOnly || weeklyLoading;

  const primaryActionHint = useMemo(() => {
    if (readOnly && submitActionKind === SUBMIT_ACTION.SUBMIT_BLOCKED) {
      return t("app.timesheets.entry.submitBlockedFooterHint");
    }
    if (readOnly) return t("app.timesheets.entry.approvedFooterHint");
    if (submitActionKind === SUBMIT_ACTION.SUBMIT_BLOCKED) return t("app.timesheets.entry.submitBlockedFooterHint");
    if (submitActionKind === SUBMIT_ACTION.UNSUBMIT) return t("app.timesheets.entry.submittedPendingHint");
    if (submitActionKind === SUBMIT_ACTION.RESUBMIT) return t("app.timesheets.entry.resubmitFooterHint");
    return t("app.timesheets.entry.submitHint");
  }, [readOnly, submitActionKind, t]);

  const statusBannerConfig = useMemo(() => {
    if (!statusBannerKind || weeklyLoading) return null;
    switch (statusBannerKind) {
      case WEEKLY_STATUS_BANNER.APPROVED:
        return {
          modifier: "approved",
          iconClass: "bi bi-check-circle-fill",
          title: t("app.timesheets.entry.statusBannerApproved"),
          body: t("app.timesheets.entry.approvedReadOnlyHint"),
        };
      case WEEKLY_STATUS_BANNER.REJECTED_BLOCKED:
        return {
          modifier: "rejected-blocked",
          iconClass: "bi bi-x-octagon-fill",
          title: t("app.timesheets.entry.statusBannerRejectedBlocked"),
          body: t("app.timesheets.entry.statusBannerRejectedBlockedHelp"),
        };
      case WEEKLY_STATUS_BANNER.REJECTED_RESUBMIT:
        return {
          modifier: "rejected-resubmit",
          iconClass: "bi bi-exclamation-triangle-fill",
          title: t("app.timesheets.entry.statusBannerRejectedResubmit"),
          body: t("app.timesheets.entry.statusBannerRejectedResubmitHelp"),
        };
      case WEEKLY_STATUS_BANNER.RETURNED:
        return {
          modifier: "returned",
          iconClass: "bi bi-arrow-return-left",
          title: t("app.timesheets.entry.statusBannerReturned"),
          body: t("app.timesheets.entry.statusBannerReturnedHelp"),
        };
      case WEEKLY_STATUS_BANNER.SUBMITTED:
        return {
          modifier: "submitted",
          iconClass: "bi bi-hourglass-split",
          title: t("app.timesheets.entry.statusBannerSubmitted"),
          body: t("app.timesheets.entry.statusBannerSubmittedHelp"),
        };
      default:
        return null;
    }
  }, [statusBannerKind, weeklyLoading, t]);

  return (
    <div className="ts-weekly-entry-form">
      {projectsLoading && !weeklyLoading ? (
        <p className="documents-page-description small text-muted mb-2 d-flex align-items-center gap-2" role="status">
          <span className="spinner-border spinner-border-sm text-primary flex-shrink-0" aria-hidden />
          <span>{t("app.timesheets.entry.projectsLoading")}</span>
        </p>
      ) : null}
      {projectsError && !projectsLoading ? (
        <p className="small text-danger mb-2" role="alert">
          {projectsError}
        </p>
      ) : null}

      {statusBannerConfig ? (
        <div
          className={`ts-weekly-status-banner ts-weekly-status-banner--${statusBannerConfig.modifier}`}
          role="status"
          aria-live="polite"
        >
          <span className="ts-weekly-status-banner__icon" aria-hidden>
            <i className={statusBannerConfig.iconClass} />
          </span>
          <div className="ts-weekly-status-banner__content">
            <span className="ts-weekly-status-banner__title">{statusBannerConfig.title}</span>
            <span className="ts-weekly-status-banner__body">{statusBannerConfig.body}</span>
          </div>
        </div>
      ) : null}

      <div className="ts-card ts-weekly-grid-card" style={{ marginBottom: "1.5rem" }}>
        <p className="ts-weekly-hours-hint">{t("app.timesheets.entry.hoursPerDayHint", { max: MAX_HOURS_PER_DAY })}</p>
        <div style={{ overflowX: "auto" }}>
          <table className="ts-weekly-table ts-weekly-table--entry">
            <thead>
              <tr>
                <th className="ts-weekly-th-project">{t("app.timesheets.entry.colProject")}</th>
                {dayDates.map((d, i) => {
                  return (
                    <th key={d.toISOString()} className={columnIsNonBusinessDay(i) ? "ts-weekend" : ""}>
                      {new Intl.DateTimeFormat(undefined, { weekday: "short" }).format(d)}{" "}
                      <span style={{ display: "block", opacity: 0.65, fontWeight: 600 }}>{d.getDate()}</span>
                    </th>
                  );
                })}
                <th style={{ minWidth: 72 }}>{t("app.timesheets.entry.colTotal")}</th>
              </tr>
            </thead>
            <tbody>
              {weeklyLoading ? (
                <tr>
                  <td colSpan={9} className="text-center py-5 text-muted">
                    <span className="spinner-border spinner-border-sm text-primary me-2" role="status" aria-hidden />
                    {t("app.timesheets.entry.weeklyLoading")}
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center py-4">
                    {showAddRow && !readOnly ? (
                      <button
                        type="button"
                        className="ts-empty-grid-hint-btn"
                        onClick={addProjectLine}
                        disabled={formLocked || projectsLoading || cannotAddAnotherProjectLine}
                        title={cannotAddAnotherProjectLine ? t("app.timesheets.entry.allProjectsOnTimesheetHint") : undefined}
                        aria-label={t("app.timesheets.entry.addProjectLine")}
                      >
                        {t("app.timesheets.entry.emptyGridHint")}
                      </button>
                    ) : (
                      <span className="text-muted">{t("app.timesheets.entry.emptyGridHint")}</span>
                    )}
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <div className="ts-entry-project-cell">
                        {!row.projectId ? (
                          <div className="ts-entry-project-pick">
                            {projectsForUnpickedRow(row.id).length === 0 ? (
                              <p className="small text-muted mb-0 ts-entry-no-projects-left">
                                {t("app.timesheets.entry.noProjectsLeftForLine")}
                              </p>
                            ) : (
                              <select
                                className="form-select form-select-sm ts-entry-project-select"
                                value=""
                                onChange={(e) => {
                                  if (e.target.value) setRowProject(row.id, e.target.value);
                                }}
                                disabled={formLocked || projectsLoading || !projects.length}
                                aria-label={t("app.timesheets.entry.selectProjectAria")}
                              >
                                <option value="">{t("app.timesheets.entry.selectProjectPlaceholder")}</option>
                                {projectsForUnpickedRow(row.id).map((p) => (
                                  <option key={p.id} value={p.id}>
                                    {p.name}
                                    {p.teamName ? ` — ${p.teamName}` : ""}
                                  </option>
                                ))}
                              </select>
                            )}
                            <button
                              type="button"
                              className="btn btn-link btn-sm text-danger p-0 ts-entry-remove-row"
                              onClick={() => removeRow(row.id)}
                              disabled={formLocked}
                            >
                              {t("app.timesheets.entry.removeLine")}
                            </button>
                          </div>
                        ) : (
                          <div className="ts-entry-project-display ts-entry-project-display--inline">
                            <span
                              className="ts-entry-project-swatch"
                              style={{ background: row.color }}
                              aria-hidden
                            />
                            <div className="ts-entry-project-meta">
                              <div className="ts-entry-project-name">{row.projectName}</div>
                              {row.teamName ? (
                                <div className="ts-entry-team-name">{row.teamName}</div>
                              ) : null}
                            </div>
                            <input
                              type="text"
                              className="form-control form-control-sm ts-entry-task-input ts-entry-task-input--inline"
                              placeholder={t("app.timesheets.entry.taskNotePlaceholder")}
                              value={row.detail}
                              onChange={(e) => setRowDetail(row.id, e.target.value)}
                              disabled={formLocked}
                            />
                            <div className="ts-entry-project-trailing">
                              <label className="ts-entry-billable-label ts-entry-billable-label--inline mb-0">
                                <input
                                  type="checkbox"
                                  checked={row.isBillable}
                                  onChange={(e) => setRowBillable(row.id, e.target.checked)}
                                  disabled={formLocked}
                                />
                                <span>{t("app.timesheets.entry.billableRow")}</span>
                              </label>
                              <button
                                type="button"
                                className="btn btn-link btn-sm ts-entry-remove-line-icon"
                                onClick={() => removeRow(row.id)}
                                disabled={formLocked}
                                aria-label={t("app.timesheets.entry.removeLineAria")}
                                title={t("app.timesheets.entry.removeLine")}
                              >
                                <i className="bi bi-x-lg" aria-hidden />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                    {row.projectId ? (
                      <>
                        {row.hours.map((h, di) => {
                          const k = cellKey(row.id, di);
                          const draft = hourDraftByCell[k];
                          const display = draft !== undefined ? draft : formatHourDisplay(h);
                          return (
                            <td key={String(di)} className={columnIsNonBusinessDay(di) ? "ts-weekend" : ""}>
                              <input
                                className="cell-hours"
                                type="text"
                                inputMode="decimal"
                                value={display}
                                placeholder="0"
                                disabled={formLocked}
                                onChange={(e) => {
                                  const next = e.target.value;
                                  setHourDraftByCell((prev) => ({ ...prev, [k]: next }));
                                  const parsed = parseHours(next);
                                  if (parsed !== null || next === "" || next.trim() === "") {
                                    commitCell(row.id, di, next);
                                  }
                                }}
                                onFocus={() => {
                                  setHourDraftByCell((prev) => ({
                                    ...prev,
                                    [k]: h == null ? "" : formatHourDisplay(h),
                                  }));
                                }}
                                onBlur={(e) => {
                                  commitCell(row.id, di, e.target.value);
                                  setHourDraftByCell((prev) => {
                                    const next = { ...prev };
                                    delete next[k];
                                    return next;
                                  });
                                }}
                                aria-label={`${row.projectName || "Project"} ${dayDates[di].toDateString()}`}
                              />
                            </td>
                          );
                        })}
                        <td>{sumRow(row.hours).toFixed(1)}</td>
                      </>
                    ) : (
                      <>
                        <td colSpan={7} className="ts-entry-hours-placeholder">
                          <span className="ts-entry-hours-placeholder-text">{t("app.timesheets.entry.hoursHiddenUntilProject")}</span>
                        </td>
                        <td className="ts-entry-hours-placeholder-total">—</td>
                      </>
                    )}
                  </tr>
                ))
              )}
            </tbody>
            <tfoot>
              <tr>
                <td
                  style={{
                    fontWeight: 800,
                    textTransform: "uppercase",
                    fontSize: "0.75rem",
                    letterSpacing: "0.06em",
                  }}
                >
                  {t("app.timesheets.entry.dailyTotals")}
                </td>
                {dailyTotals.map((dt, i) => (
                  <td
                    key={String(i)}
                    className={columnIsNonBusinessDay(i) ? "ts-weekend" : ""}
                    style={{ textAlign: "center", fontWeight: 800 }}
                  >
                    {dt.toFixed(1)}
                  </td>
                ))}
                <td style={{ fontWeight: 900, fontSize: "1.1rem", color: "var(--ts-primary, #005db6)" }}>
                  {grandTotal.toFixed(1)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
        {showAddRow && !readOnly ? (
          <button
            type="button"
            className="ts-add-project-line"
            onClick={addProjectLine}
            disabled={formLocked || projectsLoading || cannotAddAnotherProjectLine}
            title={cannotAddAnotherProjectLine ? t("app.timesheets.entry.allProjectsOnTimesheetHint") : undefined}
          >
            <i className="bi bi-plus-circle" aria-hidden />
            {t("app.timesheets.entry.addProjectLine")}
          </button>
        ) : null}
      </div>

      <div className="ts-entry-bottom-grid">
        <div className="ts-card ts-utilization-card">
          <div className="ts-utilization-inner">
            <div className="ts-donut-wrap" aria-hidden>
              <svg className="ts-donut-svg" viewBox="0 0 128 128">
                <circle className="ts-donut-track" cx="64" cy="64" r="58" fill="none" strokeWidth="8" />
                <circle
                  className="ts-donut-fill"
                  cx="64"
                  cy="64"
                  r="58"
                  fill="none"
                  strokeWidth="8"
                  strokeDasharray="364.4"
                  strokeDashoffset={dashOffset}
                  transform="rotate(-90 64 64)"
                />
              </svg>
              <div className="ts-donut-center">
                <span className="ts-donut-pct">{utilizationPct}%</span>
                <span className="ts-donut-label">{t("app.timesheets.entry.target")}</span>
              </div>
            </div>
            <div className="ts-utilization-copy">
              <h3 className="ts-utilization-title">{t("app.timesheets.entry.utilizationTitle")}</h3>
              <p className="documents-page-description" style={{ marginBottom: "1rem" }}>
                {t("app.timesheets.entry.utilizationHint", {
                  logged: grandTotal.toFixed(1),
                  expected: EXPECTED_WEEKLY_HOURS.toFixed(1),
                })}
              </p>
              <div className="ts-utilization-split">
                <div className="ts-util-mini">
                  <p className="ts-label">{t("app.timesheets.common.billable")}</p>
                  <p className="ts-val">
                    {billableHours.toFixed(1)} <span className="ts-hrs">hrs</span>
                  </p>
                </div>
                <div className="ts-util-mini">
                  <p className="ts-label">{t("app.timesheets.entry.nonBillable")}</p>
                  <p className="ts-val">
                    {nonBillableHours.toFixed(1)} <span className="ts-hrs">hrs</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="ts-card ts-submit-card">
          <p className="documents-page-description" style={{ marginBottom: "1rem", textAlign: "center" }}>
            {primaryActionHint}
          </p>
          <button
            type="button"
            className="btn btn-light documents-btn w-100 mb-2 d-inline-flex align-items-center justify-content-center gap-2"
            onClick={handleSaveDraftClick}
            disabled={formLocked}
            aria-busy={savingMode === "draft"}
          >
            {savingMode === "draft" ? (
              <>
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden />
                <span>{t("app.timesheets.entry.savingDraft")}</span>
              </>
            ) : (
              t("app.timesheets.entry.saveDraft")
            )}
          </button>
          <button
            type="button"
            className="btn documents-btn ts-ts-primary-gradient w-100 d-inline-flex align-items-center justify-content-center gap-2"
            onClick={handlePrimaryActionClick}
            disabled={formLocked || submitActionKind === SUBMIT_ACTION.SUBMIT_BLOCKED}
            aria-busy={savingMode === "submit" || savingMode === "unsubmit"}
            title={
              submitActionKind === SUBMIT_ACTION.SUBMIT_BLOCKED ? t("app.timesheets.entry.submitBlockedButtonTitle") : undefined
            }
          >
            {savingMode === "unsubmit" ? (
              <>
                <span className="spinner-border spinner-border-sm text-white" role="status" aria-hidden />
                <span>{t("app.timesheets.entry.unsubmitting")}</span>
              </>
            ) : savingMode === "submit" ? (
              <>
                <span className="spinner-border spinner-border-sm text-white" role="status" aria-hidden />
                <span>
                  {submitActionKind === SUBMIT_ACTION.RESUBMIT
                    ? t("app.timesheets.entry.resubmittingApproval")
                    : t("app.timesheets.entry.submittingApproval")}
                </span>
              </>
            ) : (
              <>
                {submitActionKind === SUBMIT_ACTION.UNSUBMIT
                  ? t("app.timesheets.entry.unsubmit")
                  : submitActionKind === SUBMIT_ACTION.RESUBMIT
                    ? t("app.timesheets.entry.resubmitApproval")
                    : t("app.timesheets.entry.submitApproval")}
                {submitActionKind === SUBMIT_ACTION.SUBMIT_BLOCKED ? null : (
                  <i
                    className={submitActionKind === SUBMIT_ACTION.UNSUBMIT ? "bi bi-arrow-counterclockwise ms-1" : "bi bi-send ms-1"}
                    aria-hidden
                  />
                )}
              </>
            )}
          </button>
          <p className="ts-deadline-hint">
            <span className="ts-deadline-dot" aria-hidden />
            {t("app.timesheets.entry.deadlineHint", { date: rangeLabel })}
          </p>
        </div>
      </div>
    </div>
  );
};

export default TimeSheetWeeklyEntryForm;
